/**
 * Shared helpers for the platformer entity classes — split out of the
 * old PlatformerEntities.js monolith so each entity-category file
 * (PlayerEntity / EnemyEntities / ItemEntities / TriggerEntities)
 * stays lean and imports the same single implementations.
 */

import { defaultCollisionFor } from './PlatformerCollision.js';

/**
 * Stamp a freshly-spawned entity with its collision config — the
 * `collisionLayer` / `collisionMask` bitmask (Godot layers + masks)
 * and the `collisionShape` ({ type, scale }) used by the scene's
 * shape-aware overlap sweep. Authored overrides ride in `record.props`
 * (`collisionLayer`, `collisionMask`, `collisionShape`, `collisionScale`);
 * anything unset falls back to the per-type defaults, which reproduce
 * the original "player collides with every entity, full-box" behaviour.
 */
export function applyCollisionConfig(entity, record) {
    if (!entity) return entity;
    const props = record?.props || {};
    const def = defaultCollisionFor(record?.type);
    entity.collisionLayer = Number.isFinite(props.collisionLayer) ? props.collisionLayer : def.layer;
    entity.collisionMask  = Number.isFinite(props.collisionMask)  ? props.collisionMask  : def.mask;
    const shapeType = props.collisionShape === 'circle' ? 'circle' : 'rect';
    const scale = Number.isFinite(props.collisionScale) ? props.collisionScale : 1;
    entity.collisionShape = { type: shapeType, scale };
    return entity;
}

/** Resolve an entity slot to a sprite via the SkinResolver, or null
 *  so the caller can draw its procedural fallback. The resolver hands
 *  back a CENTRE-anchored sprite at (0,0); callers add it to a display
 *  whose origin is the entity's top-left, so re-position it to the box
 *  centre or the art would straddle the corner. Callers that need a
 *  different anchor (e.g. PlayerEntity's feet pin) override x/y/anchor
 *  after the fact. */
export function spawnSprite(theme, slotKey, w, h) {
    const sprite = theme?.createSprite?.(slotKey, w, h, { fitMode: 'contain' }) || null;
    if (sprite) {
        sprite.x = w / 2;
        sprite.y = h / 2;
    }
    return sprite;
}

/** Clamp a possibly-missing numeric prop into [lo, hi] with a default. */
export function clampNum(v, lo, hi, dflt) {
    const n = Number(v);
    if (!Number.isFinite(n)) return dflt;
    return n < lo ? lo : n > hi ? hi : n;
}

/** Parse a "#rrggbb" string into a 0xRRGGBB int (with a fallback). */
export function parseHexColor(v, dflt) {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
        const m = /^#?([0-9a-f]{6})$/i.exec(v.trim());
        if (m) return parseInt(m[1], 16);
    }
    return dflt;
}
